// src/components/pages/signinpage/SignInPage.js

import React, { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./style/SingIn.module.css";

export default function SignInPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // ✅ 중복 호출 방지를 위한 Ref (React StrictMode 등에서 두 번 호출 방지)
  const hasCalledSignin = useRef(false);

  useEffect(() => {
    // 1. 라이브러리가 아직 로딩 중이거나, 이미 인증된 상태라면 아무것도 하지 않음
    // (isLoading일 때 리디렉션을 시도하면 상태가 꼬일 수 있음)
    if (auth.isLoading) {
      return;
    }

    // 2. 이미 인증된 경우 원래 페이지로 이동
    if (auth.isAuthenticated) {
      const fromPath = location.state?.from || '/';
      navigate(fromPath, { replace: true });
      return;
    }

    // 3. 에러가 발생한 경우 무한 루프 방지를 위해 자동 리디렉션 중단
    if (auth.error) {
      console.error("Authentication Error:", auth.error);
      return; // 여기서 return하여 다시 signinRedirect를 호출하지 않도록 함
    }

    // 4. 로그인 시도 (한 번만 실행되도록 가드 설정)
    if (!hasCalledSignin.current) {
      hasCalledSignin.current = true;
      
      const fromPath = location.state?.from;
      
      auth.signinRedirect({
        state: JSON.stringify({ from: fromPath }) // state에 돌아올 경로 저장
      }).catch(err => {
        // 리디렉션 실패 시 처리
        console.error("Redirect Error:", err);
        hasCalledSignin.current = false; // 실패 시 다시 시도 가능하도록(필요 시)
      });
    }

  }, [auth.isLoading, auth.isAuthenticated, auth.error, auth, location, navigate]);

  // 로딩 중 UI
  if (auth.isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>{t('signInPage.loadingMessage')}</p>
      </div>
    );
  }

  // 에러 발생 시 UI (무한 루프 대신 에러 메시지 표시)
  if (auth.error) {
    return (
      <div className={styles.loadingContainer}>
        <p style={{ color: 'red', marginBottom: '20px' }}>
          {t('auth.loginFailed') || "Login Failed"}
        </p>
        <p>{auth.error.message}</p>
        <button 
          className="write-post-button" // 기존 스타일 활용
          onClick={() => {
            hasCalledSignin.current = false;
            auth.removeUser().then(() => {
               // 상태 초기화 후 다시 시도
               window.location.reload();
            });
          }}
        >
          {t('auth.retry') || "Retry Login"}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.loadingContainer}>
      <div className={styles.spinner}></div>
      <p>{t('signInPage.redirectingMessage') || "Redirecting to login..."}</p>
    </div>
  );
}